<template>
  <div class="grid grid-rows my-4 order-history overflow-x-auto mx-auto w-10/12">
    <!-- Message List View -->
    <div v-if="!selectedMessage">
      <div class="md:flex md:items-center md:justify-between">
        <div class="min-w-0 flex mb-2">
          <h2 class="text-2xl font-bold text-gray-900 sm:truncate sm:tracking-tight">Berichten</h2>
        </div>
        <!-- Tab Navigation -->
        <div class="flex items-center ml-auto">
          <div class="flex bg-white rounded-full border border-gray-200 p-1">
            <button
              @click="$emit('switch-view', 'dashboard')"
              class="flex items-center px-4 py-2 text-sm font-medium text-gray-600 rounded-full hover:text-gray-900 hover:bg-gray-50 transition-colors"
              :class="{ 'bg-gray-600 text-white': false }"
            >
              <ClipboardDocumentListIcon class="w-4 h-4 mr-2" />
              Dashboard
            </button>
            <button
              @click="$emit('switch-view', 'messages')"
              class="flex items-center px-4 py-2 text-sm font-medium text-gray-600 rounded-full hover:text-gray-900 hover:bg-gray-50 transition-colors"
              :class="{ 'bg-gray-600 text-white': true }"
            >
              <ChatBubbleLeftRightIcon class="w-4 h-4 mr-2" />
              Berichten
            </button>
          </div>
        </div>
      </div>

      <div v-if="messages.length === 0" class="grid grid-rows p-6 mb-6 callout">
        Er zijn geen berichten beschikbaar voor deze patiënt.
      </div>

      <tables v-else class="overflow-hidden rounded-lg pt-4">
        <template #tableHeaders>
          <thead>
            <tr class="bg-sky-50 border-x border-t border-balternative-color">
              <th
                scope="col"
                class="py-3.5 pl-4 pr-3 text-left text-base font-bold text-pdsPrimaryColor"
                v-for="header in messageHeaders"
                :key="header"
              >
                {{ header }}
              </th>
            </tr>
          </thead>
        </template>
        <template #tableBody>
          <tbody class="bg-white divide-y divide-sky-200">
            <tr
              v-for="(message, index) in messages"
              :key="`message-${index}`"
              class="hover:bg-gray-50 cursor-pointer"
              :class="{ 'font-bold': !message.read }"
              @click="selectMessage(message)"
            >
              <td class="py-3.5 pl-4 pr-3 text-base text-gray-900">
                {{ message.subject || 'Geen onderwerp' }}
              </td>
              <td class="py-3.5 pl-4 pr-3 text-base text-gray-900"></td>
              <td class="py-3.5 pl-4 pr-3 text-base text-gray-900">
                {{ $formatDate ? $formatDate(message.received_at) : formatDate(message.received_at) }}
              </td>
            </tr>
          </tbody>
        </template>
      </tables>
    </div>

    <!-- Message Detail View -->
    <div v-else class="bg-white rounded-lg shadow-md p-6">
      <div class="mb-6">
        <button
          @click="goBack"
          class="flex items-center text-pdsPrimaryColor hover:text-pdsPrimaryColor-700 font-medium mb-4"
        >
          <span class="text-lg mr-2">‹</span>
          Terug naar Berichten
        </button>
      </div>

      <div class="space-y-4">
        <div>
          <span class="font-medium text-gray-700">Onderwerp:</span>
          <div class="text-xl font-bold text-gray-900 mt-1">
            {{ selectedMessage.subject || 'Geen onderwerp' }}
          </div>
        </div>

        <div>
          <span class="font-medium text-gray-700">Datum:</span>
          <div class="text-gray-900 mt-1">
            {{ $formatDate ? $formatDate(selectedMessage.received_at) : formatDate(selectedMessage.received_at) }}
          </div>
        </div>

        <div class="pt-4">
          <span class="font-medium text-gray-700">Bericht:</span>
          <div class="text-gray-900 mt-2 leading-relaxed" v-html="selectedMessage.body || selectedMessage.content || 'Geen inhoud beschikbaar'">
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { ClipboardDocumentListIcon, ChatBubbleLeftRightIcon } from '@heroicons/vue/20/solid';

export default {
  name: 'PatientMessages',
  components: {
    ClipboardDocumentListIcon,
    ChatBubbleLeftRightIcon
  },
  props: {
    patientId: {
      type: Number
    }
  },
  emits: ['switch-view'],
  data() {
    return {
      messages: [],
      selectedMessage: null,
      messageHeaders: ['Onderwerp', 'Patiënt', 'Datum']
    };
  },
  computed: {
    baseUrl() {
      return window.location.origin;
    }
  },
  async created() {
    try {
      const { data } = await axios.get(
        `${this.baseUrl}/api/sitecore/v2/Notification/GetInboxMessages?patientId=${this.patientId}`
      );
      this.messages = data || [];
    } catch (error) {
      console.error('Error fetching messages:', error);
      this.messages = [];
    }
  },
  methods: {
    async selectMessage(message) {
      this.selectedMessage = message;

      // Mark message as read if it's not already read
      if (!message.read) {
        try {
          await axios.post(`${this.baseUrl}/api/notification/MarkMessageAsRead`, {
            patientId: this.patientId,
            messageId: message.id,
            inboxId: message.inboxId
          });

          // Update the message status locally
          message.read = true;
        } catch (error) {
          console.error('Error marking message as read:', error);
        }
      }
    },
    goBack() {
      this.selectedMessage = null;
    },
    formatDate(dateString) {
      if (!dateString) return 'Onbekend';
      try {
        return new Date(dateString).toLocaleDateString('nl-NL');
      } catch (error) {
        return 'Onbekend';
      }
    }
  }
};
</script>
